import { Chip, Paper, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useEffect, useRef, useState } from "react";
import { isArray, isString } from "lodash";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(0.75, 1.5),
        backgroundColor: theme.mode === "light" ? "#ffffff" : "#111827",
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0.5, 1),
        },
    },
    input: {
        "& .MuiOutlinedInput-root": {
            minHeight: 38,
            paddingTop: 3,
            paddingBottom: 3,
            borderRadius: 10,
            backgroundColor: theme.mode === "light" ? "#f8fafc" : "rgba(255,255,255,0.05)",
            "& fieldset": {
                borderColor: theme.mode === "light" ? "#e2e8f0" : "rgba(255,255,255,0.12)",
            },
            "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
            },
        },
        "& .MuiInputBase-input": {
            fontSize: 13,
        },
    },
    tagChip: {
        height: 20,
        marginRight: 4,
        marginTop: 2,
        marginBottom: 2,
        borderRadius: 5,
        border: "1px solid #d7dde3",
        backgroundColor: "#f4f7f9",
        color: "#2f3a45",
        fontSize: 11,
        fontWeight: 600,
        maxWidth: 150,
        "& .MuiChip-label": {
            display: "flex",
            alignItems: "center",
            gap: 4,
            paddingLeft: 6,
            paddingRight: 4,
            overflow: "hidden",
            textOverflow: "ellipsis",
        },
        "& .MuiChip-deleteIcon": {
            width: 14,
            height: 14,
            margin: "0 4px 0 -2px",
            color: "#7a8794",
            "&:hover": {
                color: "#374151",
            },
        },
    },
    tagDot: {
        width: 7,
        height: 7,
        borderRadius: 2,
        flex: "0 0 auto",
    },
    tagLabel: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
}));

export function TagsContainer({ contact }) {
    const classes = useStyles();

    const [tags, setTags] = useState([]);
    const [selecteds, setSelecteds] = useState([]);
    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])

    useEffect(() => {
        if (isMounted.current) {
            loadTags().then(() => {
                if (Array.isArray(contact.tags)) {
                    setSelecteds(contact.tags);
                } else {
                    setSelecteds([]);
                }
            });
        }
    }, [contact]);

    const createTag = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const loadTags = async () => {
        try {
            const { data } = await api.get(`/tags/list`, 
            {params: { kanban: 0}
        });
            setTags(data);
        } catch (err) {
            toastError(err);
        }
    }

    const syncTags = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags/sync`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const onChange = async (value, reason) => {
        let optionsChanged = []
        if (reason === 'create-option') {
            if (isArray(value)) {
                for (let item of value) {
                    if (item.length < 3) {
                        toastError("Tag muito curta!");
                        return;
                    }
                    if (isString(item)) {
                        const newTag = await createTag({ name: item, kanban: 0, color: getRandomHexColor() })
                        optionsChanged.push(newTag);
                    } else {
                        optionsChanged.push(item);
                    }
                }
            }
            await loadTags();
        } else {
            optionsChanged = value;
        }
        setSelecteds(optionsChanged);
        await syncTags({ contactId: contact.id, tags: optionsChanged });
    }

    function getRandomHexColor() {
        // Gerar valores aleatórios para os componentes de cor
        const red = Math.floor(Math.random() * 256); // Valor entre 0 e 255
        const green = Math.floor(Math.random() * 256); // Valor entre 0 e 255
        const blue = Math.floor(Math.random() * 256); // Valor entre 0 e 255
      
        // Converter os componentes de cor em uma cor hexadecimal
        const hexColor = `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
      
        return hexColor;
    }

    const getTagName = (option) => isString(option) ? option : option?.name || "";

    const getTagColor = (option) => {
        if (isString(option)) return "#94a3b8";
        return option?.color || "#94a3b8";
    }

    return (
        <Paper elevation={0} square className={classes.root}>
            <Autocomplete
                multiple
                size="small"
                options={tags}
                value={selecteds}
                freeSolo
                onChange={(e, v, r) => onChange(v, r)}
                getOptionLabel={(option) => getTagName(option)}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                        const tagName = getTagName(option);
                        const tagColor = getTagColor(option);

                        return (
                            <Chip
                                className={classes.tagChip}
                                label={
                                    <>
                                        <span
                                            className={classes.tagDot}
                                            style={{ backgroundColor: tagColor }}
                                        />
                                        <span className={classes.tagLabel}>{tagName}</span>
                                    </>
                                }
                                {...getTagProps({ index })}
                                size="small"
                            />
                        );
                    })
                }
                renderInput={(params) => (
                    <TextField {...params} className={classes.input} variant="outlined" placeholder="Tags" />
                )}
                PaperComponent={({ children }) => (
                    <Paper
                        style={{ width: 400, marginLeft: 6 }}
                    >
                        {children}
                    </Paper>
                )}
            />
        </Paper>
    )
}
